export type NavItem = {
  label: string
  href: string
  // Internal Next.js page + query params for updateStudioUrl (lib/studio/navigate).
  // Omit page for links that leave the Studio app (e.g. Log Out, Back to Saatchi Art,
  // or pages outside /studio) — those navigate normally via SALink.
  page?: string
  section?: string
  subSection?: string
  // Extra path prefixes that should also count as "active" for this item, beyond an exact
  // match on href — e.g. Upload Artwork should stay active while editing any artwork at
  // /studio/art/:artworkId/:section, not just on the bare /studio/upload URL.
  activePrefixes?: string[]
  // Open link in a new tab instead of navigating in place.
  openInNewTab?: boolean
  // Show an icon next to the label (for items opening in new tab)
  showIcon?: boolean
  // Logout item that requires special handling
  isLogout?: boolean
  isDisabled?: boolean
}

const stripTrailingSlash = (path: string): string =>
  path.length > 1 ? path.replace(/\/$/, '') : path
const stripQuery = (path: string): string => path.split('?')[0]

// The server route for /studio is /:section(artworks|...)? — section is optional and
// defaults to artworks, so bare /studio is the same route as /studio/artworks. Normalize
// before comparing so View Portfolio is active on either.
const STUDIO_ROOT_HREF = '/studio'
const STUDIO_DASHBOARD_HREF = '/studio/artworks'
const normalizeStudioRootPath = (pathname: string): string =>
  pathname === STUDIO_ROOT_HREF ? STUDIO_DASHBOARD_HREF : pathname

export const isActiveHref = (currentPath: string, href: string): boolean => {
  if (!href) {
    return false
  }
  const pathname = normalizeStudioRootPath(stripTrailingSlash(stripQuery(currentPath)))
  const hrefPath = normalizeStudioRootPath(stripTrailingSlash(stripQuery(href)))
  return pathname === hrefPath
}

export const isActiveNavItem = (currentPath: string, item: NavItem): boolean => {
  if (isActiveHref(currentPath, item.href)) {
    return true
  }
  const pathname = stripQuery(currentPath)
  return item.activePrefixes?.some((prefix) => pathname.startsWith(prefix)) ?? false
}

export type NavSection = {
  title: string
  id?: string
  isSingleClickableSection?: boolean
  items: NavItem[]
}

export const BACK_TO_SAATCHI_ART_HREF = '/'

export const LOGOUT_HREF = '/easel_api/auth/logout'

const STUDIO_ARTWORKS_PAGE = '/private/studio/artworks'
const STUDIO_CURATOR_NOTES_PAGE = '/private/studio/curator-notes'
const STUDIO_SALES_PAGE = '/private/studio/sales'
export const STUDIO_PROMOTIONS_PAGE = '/private/studio/promotions'
export const STUDIO_PROMOTIONS_HREF = '/studio/promotions'
const STUDIO_PROMOTIONS_EDIT_PAYMENT_METHOD_PAGE = '/private/studio/promotionEditPaymentMethods'

export const SPONSORED_LISTINGS_SECTION_TITLE = 'Sponsored Listings'
export const PAYMENT_METHODS_ITEM_LABEL = 'Payment Methods'

export const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Manage Artworks',
    items: [
      {
        label: 'My Portfolio',
        href: '/studio/artworks',
        page: STUDIO_ARTWORKS_PAGE,
        section: 'artworks',
      },
      {
        label: 'Artwork Uploader',
        href: '/studio/upload',
        page: STUDIO_ARTWORKS_PAGE,
        section: 'upload',
        activePrefixes: ['/studio/art/'],
      },
    ],
  },
  {
    title: 'Sales Dashboard',
    items: [
      {
        label: 'Active Sales',
        href: '/studio/sales/active',
        page: STUDIO_SALES_PAGE,
        section: 'sales',
        subSection: 'active',
      },
      {
        label: 'Sales History',
        href: '/studio/sales/all',
        page: STUDIO_SALES_PAGE,
        section: 'sales',
        subSection: 'all',
      },
    ],
  },
  {
    title: 'Offers Dashboard',
    isSingleClickableSection: true,
    items: [
      {
        label: 'Offers Dashboard',
        href: '/account/offers',
        openInNewTab: true,
        showIcon: true,
      },
    ],
  },
  {
    title: SPONSORED_LISTINGS_SECTION_TITLE,
    items: [
      {
        label: 'Dashboard',
        href: STUDIO_PROMOTIONS_HREF,
        page: STUDIO_PROMOTIONS_PAGE,
        section: 'promotions',
      },
      // isDisabled is resolved per artist in buildStudioNavigationLinks (helpers/navigate).
      {
        label: PAYMENT_METHODS_ITEM_LABEL,
        href: '/studio/promotions/edit-payment-methods',
        page: STUDIO_PROMOTIONS_EDIT_PAYMENT_METHOD_PAGE,
        section: 'promotions',
      },
    ],
  },
  {
    title: 'Profile Information',
    items: [
      {
        label: 'Artist Profile',
        href: '/studio/profile-information',
        page: STUDIO_ARTWORKS_PAGE,
        section: 'profile-information',
      },
      {
        label: 'Curator Notes',
        href: '/studio/curator-notes',
        page: STUDIO_CURATOR_NOTES_PAGE,
        section: 'curator-notes',
      },
    ],
  },
  {
    title: 'Addresses',
    isSingleClickableSection: true,
    items: [
      {
        label: 'Addresses',
        href: '/studio/address',
        page: STUDIO_ARTWORKS_PAGE,
        section: 'address',
      },
    ],
  },
  {
    title: 'Account',
    items: [
      {
        label: 'Tax Information',
        href: '/studio/account',
        page: STUDIO_ARTWORKS_PAGE,
        section: 'account',
      },
      {
        label: 'ID Verification',
        href: '/studio/account#id-verification',
        page: STUDIO_ARTWORKS_PAGE,
        section: 'account',
      },
      // Not a Studio app route — navigates normally, no page/section.
      {
        label: 'Saatchi Art Account',
        href: '/account/settings',
        openInNewTab: true,
        showIcon: true,
      },
      { label: 'Log Out', href: LOGOUT_HREF, isLogout: true },
    ],
  },
]

const QUICK_LINKS_BASE: NavItem[] = [
  {
    label: 'Manage Artworks',
    href: '/studio',
    page: STUDIO_ARTWORKS_PAGE,
    section: 'dashboard',
  },
  { label: 'Sales Dashboard', href: '/studio/sales', page: STUDIO_SALES_PAGE, section: 'sales' },
  // Not a Studio app route — navigates normally, no page/section.
  { label: 'Offers Dashboard', href: '/account/offers', openInNewTab: true },
  {
    label: 'Sponsored Listings',
    href: STUDIO_PROMOTIONS_HREF,
    page: STUDIO_PROMOTIONS_PAGE,
    section: 'promotions',
  },
  {
    label: 'Edit Profile',
    href: '/studio/profile-information',
    page: STUDIO_ARTWORKS_PAGE,
    section: 'profile-information',
  },
  {
    label: 'Artist Account',
    href: '/studio/account',
    page: STUDIO_ARTWORKS_PAGE,
    section: 'account',
  },
]

export const getQuickLinks = (): NavItem[] => QUICK_LINKS_BASE

export const QUICK_LINKS: NavItem[] = QUICK_LINKS_BASE
